import { InteractionStatus } from '@azure/msal-browser';
import * as FullStory from '@fullstory/browser';
import useAuthService from '@shared/hooks/useAuthService';
import { clearMark, createMark, measure, PerformanceMarkName } from '@shared/hooks/usePerformance';
import AuthProvider, { useAuth } from '@shared/providers/AuthProvider';
import { FeatureFlagProvider } from '@shared/providers/withFeatureFlag';
import { Suspense, useEffect } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';

import AuthHandler from './components/AuthHandler';
import CheckUserStatus from './components/CheckUserStatus';
import PageLoader from './components/PageLoading';
import Routing from './routing';

const InnerApp = () => {
    const { getUser, isAuthenticated } = useAuth();
    const user = getUser();

    useEffect(() => {
        createMark(PerformanceMarkName.LoginComplete);
        measure(PerformanceMarkName.LoginStart, PerformanceMarkName.LoginComplete, {
            success: true,
        });
        clearMark(PerformanceMarkName.LoginStart);
        clearMark(PerformanceMarkName.LoginComplete);
    }, []);

    return (
        <>
            <BrowserRouter>
                {isAuthenticated && user && <CheckUserStatus user={user} />}
                <Routing />
            </BrowserRouter>
        </>
    );
};

function Host() {
    const { authService, getUser, isAuthenticated, inProgress } = useAuthService();
    const user = getUser();

    useEffect(() => {
        if (process.env.FULLSTORY_ORGID) {
            FullStory.init({ orgId: process.env.FULLSTORY_ORGID });
            FullStory.event(`Microfrontend_${process.env.ENV_NAME ?? 'dev'}_Started`, {});
        }
        window.MPULSE
            ? (window.MPULSE.ABTest = 'auctions-microfrontend')
            : (window.MPULSE = { ABTest: 'auctions-microfrontend' });
    }, []);

    useEffect(() => {
        if (process.env.FULLSTORY_ORGID && isAuthenticated() && user) {
            FullStory.setUserVars({
                displayName: user.username,
                email: user.username,
                ciamId: user.consumerResourceId,
            });
        }
    }, [isAuthenticated, user]);

    if (inProgress !== InteractionStatus.None) {
        return (
            <AuthProvider authService={authService}>
                <BrowserRouter>
                    <Routes>
                        <Route path="*" element={<AuthHandler />} />
                    </Routes>
                </BrowserRouter>
            </AuthProvider>
        );
    }

    return (
        <AuthProvider authService={authService}>
            <Suspense fallback={<PageLoader label="toggles-loading" />}>
                <FeatureFlagProvider user={user} wrappedComponent={InnerApp} />
            </Suspense>
        </AuthProvider>
    );
}

export default Host;
